/**
 * Results API adapter for fetching comparisons and responses
 * Includes correlation ID propagation and basic caching
 */

import type {
  Comparison,
  ComparisonsResponse,
  ResponsesResponse,
  ComparisonFilters,
  Response,
} from '../types/results';
import { logger } from '../logging';

interface RawComparison {
  id: string;
  userId: string;
  title: string;
  slug?: string | null;
  description?: string | null;
  summary?: string | null;
  prompt?: string | null;
  basePrompt?: string | null; // New field from API
  status: 'pending' | 'running' | 'completed' | 'failed';
  totalModels?: number | null;
  completedModels?: number | null;
  totalCost?: number | null;
  createdAt: string;
  updatedAt: string;
  isFavorite?: boolean | null;
  modelsUsed?: string[] | null;
  score?: number | null;
}

interface RawComparisonsResponse {
  comparisons: RawComparison[];
  pagination: ComparisonsResponse['pagination'];
}

interface RawResponse {
  id: string;
  comparisonId: string;
  modelId: string;
  response: string;
  status: 'pending' | 'completed' | 'failed';
  tokenUsage?: string | Record<string, any> | null;
  tokenCount?: number | null;
  cost?: number | null;
  responseTime?: number | null;
  latencyMs?: number | null;
  errorMessage?: string | null;
  createdAt: string;
  updatedAt: string;
  scoreOverall?: number | null;
  scores?: Record<string, number> | null;
  content?: string | null;
  rawResponse?: unknown;
  providerId?: string | null;
  providerName?: string | null;
  model?: {
    id: string;
    providerId: string;
    name?: string | null;
    displayName?: string | null;
  } | null;
  provider?: {
    id: string;
    name?: string | null;
  } | null;
}

interface RawResponsesResponse {
  comparisonId: string;
  responses: RawResponse[];
  count: number;
}

interface FetchResponsesOptions {
  /**
   * When true, always hit the API even if a cached payload exists.
   * The fresh payload is still written back to the cache to keep it warm.
   */
  bypassCache?: boolean;
  /**
   * Optional correlation identifier propagated to diagnostic logs.
   */
  correlationId?: string;
}

export interface ResponsesFetchMeta {
  source: 'network' | 'cache' | 'not-modified';
  etag: string | null;
  timestamp: number;
}

// API Configuration
// - In development: uses localhost:3000
// - In production: uses same-origin (empty VITE_API_URL = relative /api/* URLs)
// - VITE_ENABLE_DB_API=true explicitly enables API (set in .env.production)
const API_ENABLED =
  import.meta.env.VITE_ENABLE_DB_API === 'true' ||
  import.meta.env.MODE === 'development' ||
  (import.meta.env.MODE === 'production' && import.meta.env.VITE_API_URL !== undefined);

const API_BASE_URL = import.meta.env.VITE_API_URL !== undefined
  ? import.meta.env.VITE_API_URL  // Use specified URL (empty string = same-origin /api/*)
  : 'http://localhost:3000';       // Development fallback

/**
 * Generate a correlation ID for request tracking
 */
function generateCorrelationId(): string {
  return `client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * In-memory cache for the current page view
 * Cache is cleared when the page is refreshed
 */
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_TTL = 30000; // 30 seconds (matches server cache)

/**
 * Get cached data if available and not expired
 */
function getCached<T>(key: string): T | null {
  const cached = cache.get(key);
  if (!cached) return null;

  const isExpired = Date.now() - cached.timestamp > CACHE_TTL;
  if (isExpired) {
    cache.delete(key);
    return null;
  }

  return cached.data as T;
}

/**
 * Set cache data
 */
function setCache<T>(key: string, data: T): void {
  cache.set(key, { data, timestamp: Date.now() });
}

// ---------------------------------------------------------------------------
// Comparison response caching (memory + IndexedDB persistence)
// ---------------------------------------------------------------------------

export const ENABLE_COMPARISON_RESPONSE_CACHE =
  (import.meta.env.VITE_ENABLE_COMPARISON_RESPONSE_CACHE ?? 'true') !== 'false';

const RESPONSE_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
const RESPONSE_DB_NAME = 'smart-prompt-response-cache';
const RESPONSE_DB_VERSION = 1;
const RESPONSE_STORE = 'comparisonResponses';
const RESPONSE_META_STORE = 'comparisonResponseMeta';

interface CachedResponseEntry {
  modelId: string;
  response: Response;
  etag: string | null;
  timestamp: number;
}

interface CachedComparisonPayload {
  comparisonId: string;
  responses: CachedResponseEntry[];
  datasetEtag: string | null;
  lastUpdated: number;
}

const responseMemoryCache = new Map<string, CachedComparisonPayload>();
let responseDbPromise: Promise<IDBDatabase | null> | null = null;

function cloneResponseEntry(entry: CachedResponseEntry): CachedResponseEntry {
  return {
    modelId: entry.modelId,
    etag: entry.etag,
    timestamp: entry.timestamp,
    response: typeof structuredClone === 'function'
      ? structuredClone(entry.response)
      : JSON.parse(JSON.stringify(entry.response)),
  };
}

function clonePayload(payload: CachedComparisonPayload): CachedComparisonPayload {
  return {
    comparisonId: payload.comparisonId,
    datasetEtag: payload.datasetEtag,
    lastUpdated: payload.lastUpdated,
    responses: payload.responses.map(cloneResponseEntry),
  };
}

function isPayloadExpired(payload: CachedComparisonPayload): boolean {
  return Date.now() - payload.lastUpdated > RESPONSE_CACHE_MAX_AGE_MS;
}

function getResponseCacheKey(comparisonId: string, modelId: string): string {
  return `${comparisonId}:${modelId}`;
}

async function getResponseDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined' || !ENABLE_COMPARISON_RESPONSE_CACHE) {
    return null;
  }

  if (!responseDbPromise) {
    responseDbPromise = new Promise((resolve) => {
      try {
        const request = indexedDB.open(RESPONSE_DB_NAME, RESPONSE_DB_VERSION);
        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(RESPONSE_STORE)) {
            const store = db.createObjectStore(RESPONSE_STORE, { keyPath: 'key' });
            store.createIndex('comparisonId', 'comparisonId', { unique: false });
          }
          if (!db.objectStoreNames.contains(RESPONSE_META_STORE)) {
            db.createObjectStore(RESPONSE_META_STORE, { keyPath: 'comparisonId' });
          }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
      } catch (error) {
        console.warn('[API] Failed to open IndexedDB for responses', error);
        resolve(null);
      }
    });
  }

  return responseDbPromise;
}

async function removeResponsesFromPersistence(comparisonId: string): Promise<void> {
  const db = await getResponseDb();
  if (!db) {
    return;
  }

  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction([RESPONSE_STORE, RESPONSE_META_STORE], 'readwrite');
      tx.objectStore(RESPONSE_META_STORE).delete(comparisonId);
      const responsesStore = tx.objectStore(RESPONSE_STORE);
      const index = responsesStore.index('comparisonId');
      const cursorRequest = index.openCursor(IDBKeyRange.only(comparisonId));
      cursorRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue | null>).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch (error) {
      console.warn('[API] Failed to purge IndexedDB cache', error);
      resolve();
    }
  });
}

function setResponseMemoryCache(payload: CachedComparisonPayload): void {
  responseMemoryCache.set(payload.comparisonId, payload);
}

export function getComparisonResponsesFromMemory(
  comparisonId: string
): CachedComparisonPayload | null {
  const payload = responseMemoryCache.get(comparisonId);
  if (!payload) {
    return null;
  }

  if (isPayloadExpired(payload)) {
    responseMemoryCache.delete(comparisonId);
    void removeResponsesFromPersistence(comparisonId);
    return null;
  }

  return clonePayload(payload);
}

async function getComparisonResponsesFromPersistence(
  comparisonId: string
): Promise<CachedComparisonPayload | null> {
  const db = await getResponseDb();
  if (!db) {
    return null;
  }

  return new Promise((resolve) => {
    try {
      const tx = db.transaction([RESPONSE_STORE, RESPONSE_META_STORE], 'readonly');
      const responsesStore = tx.objectStore(RESPONSE_STORE);
      const metaStore = tx.objectStore(RESPONSE_META_STORE);
      const responsesRequest = responsesStore.index('comparisonId').getAll(IDBKeyRange.only(comparisonId));
      const metaRequest = metaStore.get(comparisonId);

      tx.oncomplete = () => {
        const meta = metaRequest.result as { datasetEtag: string | null; lastUpdated: number } | undefined;
        const entries = (responsesRequest.result as Array<{
          key: string;
          comparisonId: string;
          modelId: string;
          response: Response;
          etag: string | null;
          timestamp: number;
        }> | undefined) ?? [];

        if (!meta || entries.length === 0) {
          resolve(null);
          return;
        }

        const payload: CachedComparisonPayload = {
          comparisonId,
          datasetEtag: meta.datasetEtag ?? null,
          lastUpdated: meta.lastUpdated ?? 0,
          responses: entries.map(entry => ({
            modelId: entry.modelId,
            response: entry.response,
            etag: entry.etag ?? null,
            timestamp: entry.timestamp,
          })),
        };

        if (isPayloadExpired(payload)) {
          void removeResponsesFromPersistence(comparisonId);
          resolve(null);
          return;
        }

        resolve(clonePayload(payload));
      };

      tx.onerror = () => resolve(null);
    } catch (error) {
      console.warn('[API] Failed to read IndexedDB cache', error);
      resolve(null);
    }
  });
}

async function persistComparisonResponses(payload: CachedComparisonPayload): Promise<void> {
  const db = await getResponseDb();
  if (!db) {
    return;
  }

  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction([RESPONSE_STORE, RESPONSE_META_STORE], 'readwrite');
      const responsesStore = tx.objectStore(RESPONSE_STORE);
      const metaStore = tx.objectStore(RESPONSE_META_STORE);
      const newKeys = new Set(
        payload.responses.map(entry => getResponseCacheKey(payload.comparisonId, entry.modelId))
      );

      const cursorRequest = responsesStore
        .index('comparisonId')
        .openCursor(IDBKeyRange.only(payload.comparisonId));

      cursorRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue | null>).result;
        if (cursor) {
          const key = cursor.primaryKey as string;
          if (!newKeys.has(key)) {
            cursor.delete();
          }
          cursor.continue();
          return;
        }

        for (const entry of payload.responses) {
          responsesStore.put({
            key: getResponseCacheKey(payload.comparisonId, entry.modelId),
            comparisonId: payload.comparisonId,
            modelId: entry.modelId,
            response: entry.response,
            etag: entry.etag ?? null,
            timestamp: entry.timestamp,
          });
        }

        metaStore.put({
          comparisonId: payload.comparisonId,
          datasetEtag: payload.datasetEtag ?? null,
          lastUpdated: payload.lastUpdated,
        });
      };

      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch (error) {
      console.warn('[API] Failed to persist responses cache', error);
      resolve();
    }
  });
}

export async function hydrateComparisonResponses(
  comparisonId: string
): Promise<CachedComparisonPayload | null> {
  if (!ENABLE_COMPARISON_RESPONSE_CACHE) {
    return null;
  }

  const fromMemory = getComparisonResponsesFromMemory(comparisonId);
  if (fromMemory) {
    return fromMemory;
  }

  const persisted = await getComparisonResponsesFromPersistence(comparisonId);
  if (persisted) {
    setResponseMemoryCache(persisted);
    return clonePayload(persisted);
  }

  return null;
}

async function writeComparisonResponsesToCache(payload: CachedComparisonPayload): Promise<void> {
  if (!ENABLE_COMPARISON_RESPONSE_CACHE) {
    return;
  }

  setResponseMemoryCache(payload);
  await persistComparisonResponses(payload);
}

function buildCachedPayload(
  comparisonId: string,
  responses: Response[],
  datasetEtag: string | null,
  timestamp: number
): CachedComparisonPayload {
  return {
    comparisonId,
    datasetEtag,
    lastUpdated: timestamp,
    responses: responses.map(response => ({
      modelId: response.modelId,
      response,
      etag: datasetEtag,
      timestamp,
    })),
  };
}

export function clearResponseCache(): void {
  responseMemoryCache.clear();
  if (typeof indexedDB !== 'undefined') {
    void indexedDB.deleteDatabase(RESPONSE_DB_NAME);
  }
  responseDbPromise = null;
}

/**
 * Get Firebase ID token for API authentication
 * Returns null if user is not authenticated
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const { auth } = await import('../firebase/config');
    const user = auth.currentUser;
    if (!user) {
      return null;
    }
    return await user.getIdToken();
  } catch (error) {
    console.warn('Failed to get auth token:', error);
    return null;
  }
}

/**
 * Fetch with correlation ID and error handling
 * Automatically includes Firebase auth token if user is authenticated
 */
async function fetchWithCorrelation<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const correlationId = generateCorrelationId();

  // Get Firebase auth token
  const authToken = await getAuthToken();
  const authHeaders = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  const response = await fetch(url, {
    ...options,
    // CRITICAL: Include credentials to send sp_anon_id cookie for anonymous user tracking
    // This enables backend to filter comparisons by anonymous session ID
    credentials: 'include',
    // CRITICAL FIX: Bypass browser HTTP cache to prevent stale data after comparison creation
    // Without this, browser serves cached /api/comparisons response that doesn't include
    // newly created comparisons, causing redirect to list view instead of detail view
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      'X-Correlation-Id': correlationId,
      ...authHeaders,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: 'Request failed',
      details: response.statusText,
    }));
    throw new Error(error.details || error.error || 'Request failed');
  }

  return response.json();
}

/**
 * Fetch comparisons with pagination and sorting
 */
export async function fetchComparisons(
  filters: ComparisonFilters = {}
): Promise<ComparisonsResponse> {
  // HOTFIX: If API is disabled (prod without server), throw error to trigger demo fallback
  if (!API_ENABLED) {
    throw new Error('API not available - using demo mode');
  }

  const { comparisonId, limit = 20, offset = 0, sort = '-createdAt' } = filters;

  const cacheKey = `comparisons:${comparisonId || 'all'}:${limit}:${offset}:${sort}`;
  const cached = getCached<ComparisonsResponse>(cacheKey);
  if (cached) {
    logger.info('comparisons_cache_hit', {
      cacheKey,
      comparisonId,
      count: cached.comparisons.length,
      hasMore: cached.pagination.hasMore
    }, {
      op: 'fetchComparisons',
      component: 'results_api',
      success: true
    });
    console.log('[API] Using cached comparisons:', cacheKey);
    return cached;
  }

  logger.info('comparisons_cache_miss', {
    cacheKey,
    comparisonId,
    limit,
    offset,
    sort
  }, {
    op: 'fetchComparisons',
    component: 'results_api',
    success: true
  });

  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
    sort,
  });

  if (comparisonId) {
    params.set('comparisonId', comparisonId);
  }

  const url = `${API_BASE_URL}/api/comparisons?${params}`;
  const startTime = Date.now();
  const rawData = await fetchWithCorrelation<RawComparisonsResponse>(url);

  console.log('[fetchComparisons] API Response:', {
    url,
    comparisonId,
    comparisonsCount: rawData.comparisons.length,
    comparisons: rawData.comparisons,
    pagination: rawData.pagination
  });

  logger.info('comparisons_network_fetch', {
    url,
    count: rawData.comparisons.length,
    duration_ms: Date.now() - startTime,
    cacheKey
  }, {
    op: 'fetchComparisons',
    component: 'results_api',
    success: true
  });

  const normalized: ComparisonsResponse = {
    comparisons: rawData.comparisons.map<Comparison>((comparison) => {
      // Prefer prompt, fallback to basePrompt, warn if both missing
      const prompt = comparison.prompt || comparison.basePrompt || '';

      if (!comparison.prompt && !comparison.basePrompt) {
        logger.warn('comparison_missing_prompt', { comparisonId: comparison.id }, {
          op: 'fetchComparisons',
          component: 'results_api_adapter',
          success: false
        });
      }

      return {
        id: comparison.id,
        userId: comparison.userId,
        title: comparison.title,
        slug: comparison.slug ?? undefined,
        prompt,
        status: comparison.status,
        totalModels:
          typeof comparison.totalModels === 'number'
            ? comparison.totalModels
            : Array.isArray(comparison.modelsUsed)
              ? comparison.modelsUsed.length
              : 0,
        completedModels:
          typeof comparison.completedModels === 'number'
            ? comparison.completedModels
            : typeof comparison.totalModels === 'number'
              ? comparison.totalModels
              : Array.isArray(comparison.modelsUsed)
                ? comparison.modelsUsed.length
                : 0,
        totalCost: comparison.totalCost ?? null,
        createdAt: comparison.createdAt,
        updatedAt: comparison.updatedAt,
        isFavorite: comparison.isFavorite ?? undefined,
        description: comparison.description ?? undefined,
        modelsUsed: comparison.modelsUsed ?? undefined,
        score: comparison.score ?? null,
      };
    }),
    pagination: rawData.pagination,
  };

  setCache(cacheKey, normalized);
  return normalized;
}

/**
 * Fetch responses for a specific comparison
 */
export async function fetchResponses(
  comparisonId: string,
  options: FetchResponsesOptions = {}
): Promise<ResponsesResponse & { meta: ResponsesFetchMeta }> {
  const { bypassCache = false, correlationId } = options;
  const cacheEnabled = ENABLE_COMPARISON_RESPONSE_CACHE && !bypassCache;
  const fetchStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();

  const url = `${API_BASE_URL}/api/comparisons/${comparisonId}/responses`;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Correlation-Id': correlationId ?? generateCorrelationId(),
  };

  let cachedPayload: CachedComparisonPayload | null = null;
  if (cacheEnabled) {
    cachedPayload = getComparisonResponsesFromMemory(comparisonId);
    if (cachedPayload?.datasetEtag) {
      headers['If-None-Match'] = cachedPayload.datasetEtag;
    }
  } else {
    logger.debug('responses_fetch_cache_bypassed', { comparisonId }, {
      correlationId,
      op: 'responses_fetch',
      component: 'resultsApi'
    });
  }

  logger.info('responses_fetch_start', {
    comparisonId,
    hasCached: cacheEnabled && !!cachedPayload,
  }, {
    correlationId,
    op: 'responses_fetch',
    component: 'resultsApi'
  });

  let httpResponse: globalThis.Response;
  try {
    httpResponse = await fetch(url, { headers });
  } catch (error) {
    logger.error('responses_fetch_error', { comparisonId }, {
      correlationId,
      op: 'responses_fetch',
      component: 'resultsApi',
      success: false,
      err_message: error instanceof Error ? error.message : 'Unknown error'
    });
    throw error;
  }

  const finish = () =>
    (typeof performance !== 'undefined' ? performance.now() : Date.now()) - fetchStartedAt;

  if (httpResponse.status === 304) {
    const timestamp = cachedPayload?.lastUpdated ?? Date.now();
    const normalized: ResponsesResponse = {
      comparisonId,
      responses: cachedPayload ? cachedPayload.responses.map(entry => cloneResponseEntry(entry).response) : [],
      count: cachedPayload ? cachedPayload.responses.length : 0,
    };

    logger.info('responses_fetch_not_modified', { comparisonId, count: normalized.count }, {
      correlationId,
      op: 'responses_fetch',
      component: 'resultsApi',
      success: true,
      duration_ms: finish(),
    });

    logger.info('response_refresh', {
      comparisonId,
      status: 'not_modified',
      count: normalized.count,
    }, {
      correlationId,
      op: 'responses_fetch',
      component: 'resultsApi',
      success: true,
    });

    return {
      ...normalized,
      meta: {
        source: 'not-modified',
        etag: cachedPayload?.datasetEtag ?? null,
        timestamp,
      },
    };
  }

  if (!httpResponse.ok) {
    let errorMessage = 'Request failed';
    try {
      const errorBody = await httpResponse.json();
      errorMessage = errorBody?.details ?? errorBody?.error ?? errorMessage;
    } catch (error) {
      // swallow JSON parsing errors and use default message
    }

    logger.error('responses_fetch_error', { comparisonId }, {
      correlationId,
      op: 'responses_fetch',
      component: 'resultsApi',
      success: false,
      err_message: errorMessage,
    });

    throw new Error(errorMessage);
  }

  let rawData: RawResponsesResponse;
  try {
    rawData = await httpResponse.json();
  } catch (error) {
    logger.error('responses_fetch_error', { comparisonId }, {
      correlationId,
      op: 'responses_fetch',
      component: 'resultsApi',
      success: false,
      err_message: 'Failed to parse response JSON'
    });
    throw error instanceof Error ? error : new Error('Failed to parse response JSON');
  }

  const normalizedResponses = rawData.responses.map<Response>((item) => {
    let parsedTokenUsage: Record<string, any> | null = null;
    if (typeof item.tokenUsage === 'string') {
      try {
        parsedTokenUsage = JSON.parse(item.tokenUsage);
      } catch (error) {
        console.warn('[API] Failed to parse token usage JSON', error);
      }
    } else if (item.tokenUsage && typeof item.tokenUsage === 'object') {
      parsedTokenUsage = item.tokenUsage;
    }

    const tokenCountCandidates: Array<number | null | undefined> = [
      item.tokenCount,
      parsedTokenUsage?.totalTokens,
      parsedTokenUsage?.total,
    ];

    let tokenCount: number | null = null;
    for (const candidate of tokenCountCandidates) {
      if (typeof candidate === 'number' && Number.isFinite(candidate)) {
        tokenCount = candidate;
        break;
      }
    }

    const providerId = item.providerId ?? item.model?.providerId ?? item.provider?.id ?? 'unknown-provider';
    const providerName = item.providerName ?? item.provider?.name ?? null;

    const rawResponse = item.rawResponse;
    const contentCandidates: Array<unknown> = [
      item.content,
      item.response,
    ];

    if (rawResponse && typeof rawResponse === 'object') {
      const rawObject = rawResponse as Record<string, unknown>;
      contentCandidates.push(rawObject.content, rawObject.text);
    }

    let content = '';
    for (const candidate of contentCandidates) {
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        content = candidate;
        break;
      }
    }

    const responseText =
      typeof item.response === 'string' && item.response.trim().length > 0
        ? item.response
        : content;

    return {
      id: item.id,
      comparisonId: item.comparisonId,
      modelId: item.modelId,
      providerId,
      providerName,
      content,
      response: responseText,
      status: item.status,
      tokenCount,
      tokenUsage: parsedTokenUsage,
      latencyMs: item.latencyMs ?? item.responseTime ?? null,
      responseTime: item.responseTime ?? item.latencyMs ?? null,
      cost: item.cost ?? null,
      scoreOverall: item.scoreOverall ?? null,
      scores: item.scores ?? null,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      rawResponse,
      errorMessage: item.errorMessage ?? null,
      model: item.model ?? null,
      provider: item.provider ?? null,
    };
  });

  const normalized: ResponsesResponse = {
    comparisonId: rawData.comparisonId,
    responses: normalizedResponses,
    count: rawData.count,
  };

  const datasetEtag = httpResponse.headers.get('ETag');
  const timestamp = Date.now();

  if (cacheEnabled) {
    const payload = buildCachedPayload(comparisonId, normalizedResponses, datasetEtag, timestamp);
    await writeComparisonResponsesToCache(payload);
  }

  logger.info('responses_fetch_success', {
    comparisonId,
    count: normalized.count,
  }, {
    correlationId,
    op: 'responses_fetch',
    component: 'resultsApi',
    success: true,
    duration_ms: finish(),
  });

  logger.info('response_refresh', {
    comparisonId,
    status: 'updated',
    count: normalized.count,
  }, {
    correlationId,
    op: 'responses_fetch',
    component: 'resultsApi',
    success: true,
  });

  return {
    ...normalized,
    meta: {
      source: 'network',
      etag: datasetEtag,
      timestamp,
    },
  };
}

/**
 * Clear all cached data (useful for forced refresh)
 */
export function clearCache(): void {
  const cacheSize = cache.size;
  cache.clear();
  clearResponseCache();

  logger.info('cache_cleared', {
    inMemoryCacheSize: cacheSize,
    timestamp: Date.now(),
    caller: 'clearCache'
  }, {
    op: 'cache_cleared',
    component: 'results_api',
    success: true
  });

  console.log('[API] Cache cleared:', { inMemoryCacheSize: cacheSize });
}

/**
 * Update comparison title and/or description
 */
export interface UpdateComparisonRequest {
  title?: string;
  description?: string;
}

export async function updateComparison(
  comparisonId: string,
  updates: UpdateComparisonRequest
): Promise<{ success: boolean; comparison: Comparison }> {
  // HOTFIX: If API is disabled (prod without server), throw error
  if (!API_ENABLED) {
    throw new Error('API not available - update not supported in demo mode');
  }

  const url = `${API_BASE_URL}/api/v1/comparisons/${comparisonId}`;

  const data = await fetchWithCorrelation<{
    success: boolean;
    comparison: RawComparison;
  }>(url, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });

  // Invalidate comparisons cache after update (same pattern as toggleFavorite)
  Array.from(cache.keys()).forEach(key => {
    if (key.startsWith('comparisons:')) {
      cache.delete(key);
    }
  });

  console.log('[API] Comparison updated:', comparisonId, Object.keys(updates));

  // Normalize the comparison response
  const normalizedComparison: Comparison = {
    id: data.comparison.id,
    userId: data.comparison.userId,
    title: data.comparison.title,
    slug: data.comparison.slug ?? undefined,
    prompt: data.comparison.prompt ?? data.comparison.basePrompt ?? '',
    status: data.comparison.status,
    totalModels: data.comparison.totalModels ?? 0,
    completedModels: data.comparison.completedModels ?? 0,
    totalCost: data.comparison.totalCost ?? null,
    createdAt: data.comparison.createdAt,
    updatedAt: data.comparison.updatedAt,
    description: data.comparison.description ?? undefined,
    modelsUsed: data.comparison.modelsUsed ?? undefined,
    score: data.comparison.score ?? null,
  };

  return {
    success: data.success,
    comparison: normalizedComparison
  };
}

/**
 * Toggle favorite status for a comparison
 * STEP 7: DB-persisted favorite with optimistic UI
 */
export async function toggleFavorite(
  comparisonId: string,
  isFavorite: boolean
): Promise<{ success: boolean; comparisonId: string; isFavorite: boolean }> {
  // HOTFIX: If API is disabled (prod without server), throw error to use localStorage only
  if (!API_ENABLED) {
    throw new Error('API not available - using localStorage only');
  }

  const url = `${API_BASE_URL}/api/comparisons/${comparisonId}/favorite`;

  const data = await fetchWithCorrelation<{
    success: boolean;
    comparisonId: string;
    isFavorite: boolean;
  }>(url, {
    method: 'PATCH',
    body: JSON.stringify({ isFavorite }),
  });

  // Invalidate comparisons cache after favorite toggle
  Array.from(cache.keys()).forEach(key => {
    if (key.startsWith('comparisons:')) {
      cache.delete(key);
    }
  });

  console.log('[API] Favorite toggled:', comparisonId, isFavorite);
  return data;
}

export interface JudgeModelInfo {
  provider: string;
  model: string;
  displayName?: string;
}

/**
 * Fetch judge models used to score a comparison
 * Returns array of {provider, model, displayName?} objects
 */
export async function getJudgeModelsForComparison(
  comparisonId: string
): Promise<JudgeModelInfo[]> {
  try {
    const response = await fetch(`/api/comparisons/${comparisonId}/judge-models`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.error('[API] Failed to fetch judge models:', response.status);
      return [];
    }

    const data = await response.json();
    return data.judges || [];
  } catch (error) {
    console.error('[API] Error fetching judge models:', error);
    return [];
  }
}

/**
 * Select judges for multi-judge scoring without actually scoring
 * Returns the judges that will be used immediately for display
 */
export async function selectJudgesForScoring(
  comparisonId: string
): Promise<JudgeModelInfo[]> {
  try {
    const response = await fetch(`/api/results/${comparisonId}/select-judges`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.error('[API] Failed to select judges:', response.status);
      return [];
    }

    const data = await response.json();
    return data.judges || [];
  } catch (error) {
    console.error('[API] Error selecting judges:', error);
    return [];
  }
}

